import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 0,
  },
  colors: {
    primary: {
      main: "#EFDF00",
      dark: "#1A1A1A",
      contrastText: "#F6F8F9",
    },
    secondary: {
      main: "#333333",
      dark: "#333333",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#EE5352",
    },
    info: {
      main: "#1FA2C8",
      light: "#DBF0F6",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#3E3F40",
      hint: "#999999",
    },

    background: "#F2F2F2",
    backgroundAlt: "#FFFFFF",
    border: "#8C8C8B",
    hover: "#F8EB4C",
    disabled: "#EEE",
    selected: "#EEE",
    selectedText: "#000",
    placeholder: "#656666",
    transparent: "#FFFFFF",
    divider: "#D9D9D6",
    dividerAlt: "#8C8C8B",
  },
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);
const fontFamily = {
  regular: "NouvelR-Regular",
  bold: "NouvelR-Bold",
};

// More properties here: https://material-ui.com/customization/default-theme/
export const renault = {
  themeName: "renault",
  brand: BrandsEnum.Renault,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  headerBackgroundColor: variables.colors.primary.dark,
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: "#F2F2F2",
      paper: "#F2F2F2",
    },
    background: {
      default: "#FFF",
      paper: variables.colors.background,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    backgroundColor: "#FFFFFF",
    hoverBackgroundColor: "#D9D9D6",
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  typography: {
    fontFamily: getFontFamily(fontFamily.regular),
    color: variables.colors.text.secondary,
    h1: {
      fontFamily: getFontFamily(fontFamily.bold),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
    },
    h2: {
      fontFamily: getFontFamily(fontFamily.bold),
      fontSize: "1.5rem",
      lineHeight: "2rem",
      color: variables.colors.text.primary,
      fontWeight: 900,
      textAlign: "center" as "center",
      paddingBottom: "2rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    h3: {
      fontFamily: getFontFamily(fontFamily.bold),
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
      textDecoration: "underline",
    },
    h4: {
      fontFamily: getFontFamily(fontFamily.bold),
      color: variables.colors.text.primary,
      fontWeight: 900,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontFamily: getFontFamily(fontFamily.bold),
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
    },
    h6: {
      fontFamily: getFontFamily(fontFamily.bold),
      color: variables.colors.text.primary,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.secondary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
      backgroundColor: "#FFFFFF",
    },
    cardSelected: {
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      backgroundColor: "#FFFFFF",
    },
    header: {
      textColor: "#000",
      fontWeight: "bold",
    },
    price: {
      fontFamily: getFontFamily(fontFamily.bold),
      fontWeight: 700,
      color: "#000",
      alignSelf: "baseline",
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  appBackgroundColor: variables.colors.background,
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiButton: {
      root: {
        fontFamily: getFontFamily(fontFamily.bold),
        fontWeight: 400,
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        border: `1px solid ${variables.colors.primary.main}`,
        boxShadow: props.buttons.boxShadow,
        color: variables.colors.primary.dark,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.text.secondary,
          backGroundColor: variables.colors.text.secondary,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.text.secondary,
          backGroundColor: variables.colors.text.secondary,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.primary.dark,
        },
        "&$disabled": {
          backgroundColor: variables.colors.disabled,
          color: variables.colors.border,
          borderColor: variables.colors.border,
        },
        borderRadius: 0,
        fontWeight: 700,
        "a&": {
          border: `1px solid ${variables.colors.primary.main}`,
          borderRadius: 0,
          "&:hover": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
          fontWeight: 700,
          textDecoration: "none",
        },
      },
      containedPrimary: {
        backgroundColor: variables.colors.primary.main,
        fontWeight: 700,
        color: variables.colors.primary.dark,
        textDecoration: "none",
        "&:hover": {
          border: "1px solid " + variables.colors.hover,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.hover,
          "& span": {
            textDecoration: "none",
          },
        },
      },
      containedSecondary: {
        backgroundColor: variables.colors.primary.dark,
        fontWeight: 400,
        color: variables.colors.secondary.contrastText,
        textDecoration: "none",
        "&:hover": {
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.text.secondary,
          "& span": {
            textDecoration: "none",
          },
        },
      },
      outlinedSecondary: {
        "&:hover": {
          borderColor: variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        "&:active": {
          borderColor: variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        "&$disabled": {
          backgroundColor: variables.colors.disabled,
          color: variables.colors.border,
          borderColor: variables.colors.border,
        },
        color: variables.colors.primary.main,
        border: "1px solid " + variables.colors.primary.main,
        borderRadius: theme.spacing(5),
        "a&": {
          "&:hover": {
            borderColor: variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            borderColor: variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          "&$disabled": {
            backgroundColor: variables.colors.disabled,
            color: variables.colors.border,
            borderColor: variables.colors.border,
          },
          color: variables.colors.primary.main,
          border: "1px solid " + variables.colors.primary.main,
          borderRadius: theme.spacing(5),
        },
      },

      text: {
        color: variables.colors.primary.dark,
        "&:hover": {
          color: variables.colors.primary.dark,
          backgroundColor: "#D9D9D6",
          textDecoration: "underline",
        },
        "&:active": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
      },
      textPrimary: {
        color: variables.colors.primary.dark,
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.dark,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.dark,
        },
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: variables.colors.primary.dark,
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.dark,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiInputBase: {
      root: {
        backgroundColor: "#FFFFFF",
      },
    },
    MuiInputLabel: {
      formControl: {
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        color: variables.colors.text.primary,
        lineHeight: 1.75,
        flexBasis: "92%",
      },
    },
    MuiOutlinedInput: {
      root: {
        "&:hover $notchedOutline": {
          borderColor: variables.colors.transparent,
          borderBottom: `1px solid ${variables.colors.primary.dark}`,
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.transparent,
          borderBottom: `2px solid ${variables.colors.primary.main}`,
        },
        "&$error $notchedOutline": {
          borderColor: variables.colors.transparent,
          borderBottom: `1px solid ${variables.colors.error.main}`,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.transparent,
        borderBottom: `1px solid ${variables.colors.primary.dark}`,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,

      button: {
        backgroundColor: variables.colors.backgroundAlt,
        "&:hover": {
          backgroundColor: variables.colors.primary.main,
        },
        "&$selected": {
          backgroundColor: variables.colors.backgroundAlt,
          "&:hover": {
            backgroundColor: variables.colors.primary.main,
          },
        },
      },
    },
    MuiMenuItem: {
      root: {
        color: "#000",
        border: `1px solid ${variables.colors.transparent}`,
        borderLeftWidth: 2,
        "&$selected": {
          borderLeftColor: variables.colors.primary.main,
        },
      },
    },
    MuiSelect: {
      root: {
        color: "#000",
        backgroundColor: "#fff",
      },
      select: {
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        //...Helpers.svgSelectorWrapper(svgIcons.arrowDown, { svgRelation: '~' }),
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
      },
      icon: {
        fill: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          color: "#1A1A1A",
          textDecoration: "underline",
          "&:hover": {
            color: "#656666",
          },
          "&:active": {
            color: "#656666",
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        fontFamily: getFontFamily(fontFamily.regular),
        color: "#000",
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: "#1A1A1A",
        border: `1px solid ${variables.colors.border}`,
        backgroundColor: "#fff",
        borderRadius: 0,
        "&$selected": {
          color: variables.colors.selectedText,
          borderColor: variables.colors.primary.main,
          fontWeight: "bold",
          backgroundColor: "#fff",
        },
        "&:hover": {
          backgroundColor: "#fff",
        },
      },
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: "#D9D9D6",
      },
    },
    MuiPickersToolbarText: {
      toolbarTxt: {
        color: variables.colors.primary.dark,
      },
      toolbarBtnSelected: {
        color: variables.colors.primary.dark,
      },
    },
    MuiPickersBasePicker: {
      container: {
        backgroundColor: "#ffff",
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
      pickerView: {
        backgroundColor: "#D9D9D6",
      },
    },
    MuiPickersCalendarHeader: {
      iconButton: {
        backgroundColor: "#D9D9D6",
      },
    },
    MuiDialogActions: {
      root: {
        backgroundColor: "#D9D9D6",
      },
    },
    MuiPickersDay: {
      daySelected: {
        color: variables.colors.primary.dark,
        "& :hover": {
          color: variables.colors.text.primary,
        },
      },
      current: {
        color: variables.colors.primary.dark,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  coverageSelect: {
    root: {
      borderColor: variables.colors.dividerAlt,
      backgroundColor: variables.colors.background,
      textColor: variables.colors.border,
    },
    selected: {
      borderColor: variables.colors.primary.main,
      backgroundColor: variables.colors.backgroundAlt,
      CoverageHeaderName: {
        fontWeight: 700,
        fontFamily: getFontFamily(fontFamily.bold),
      },
      CoverageHeaderPrice: {
        fontFamily: getFontFamily(fontFamily.bold),
        fontWeight: 700,
      },
    },
    descriptionCard: {
      backgroundColor: "#FFFFFF",
      borderColor: "#FFFFFF",
    },
    recommended: {
      backgroundColor: variables.colors.primary.dark,
      borderColor: variables.colors.primary.dark,
      textColor: variables.colors.primary.contrastText,
    },
  },
  footerBar: {
    backgroundColor: "#1A1A1A",
    textColor: "#FFFFFF",
  },
  header: {
    "& *": {
      color: "#000",
    },
    "& hr": {
      background: "none",
      margin: "12px 0px",
      border: "0px",
    },
    "& .welcome-customer-name": {
      display: "block",
    },
    "& h1": {
      margin: "0 auto",
      fontWeight: "bold;",
    },
    "& a": {
      backgroundColor: "#000",
      border: `solid 1px #000`,
      "& a.MuiButton-contained:hover": {
        backgroundColor: "#3E3F40",
      },
      "& > span": {
        color: "#FFF",
      },
    },
  },
  carInfo: {
    fontSize: "1rem",
    lineHeight: "1.25rem",
  },
  button: {
    fontFamily: getFontFamily(fontFamily.bold),
    fontWeight: 700,
    fontSize: "1rem",
    lineHeight: "1.25rem",
  },
  popup: {
    root: {
      fontFamily: getFontFamily(fontFamily.regular),
      "& p": {
        color: "#F2F2F2",
      },
    },
    h4: {
      color: "#F2F2F2",
    },
    checkMarkColor: "#FFFF", // correct later
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#1A1A1A",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#EFDF00",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily(fontFamily.bold),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  icons: {
    infoIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M11.2105 17H12.7894V10.9474H11.2105V17ZM12 9.14576C12.2409 9.14576 12.4428 9.06428 12.6058 8.90133C12.7687 8.73839 12.8502 8.53646 12.8502 8.29557C12.8502 8.05469 12.7687 7.85277 12.6058 7.6898C12.4428 7.52686 12.2409 7.44538 12 7.44538C11.7591 7.44538 11.5572 7.52686 11.3942 7.6898C11.2313 7.85277 11.1498 8.05469 11.1498 8.29557C11.1498 8.53646 11.2313 8.73839 11.3942 8.90133C11.5572 9.06428 11.7591 9.14576 12 9.14576ZM12.0018 22C10.6187 22 9.31863 21.7375 8.10165 21.2126C6.88464 20.6877 5.82603 19.9753 4.9258 19.0755C4.02555 18.1757 3.31285 17.1175 2.78771 15.9011C2.26257 14.6846 2 13.3849 2 12.0018C2 10.6187 2.26246 9.31863 2.78737 8.10165C3.31228 6.88464 4.02465 5.82603 4.92448 4.9258C5.82433 4.02555 6.88248 3.31286 8.09894 2.78771C9.31538 2.26257 10.6151 2 11.9982 2C13.3813 2 14.6814 2.26246 15.8984 2.78737C17.1154 3.31229 18.174 4.02465 19.0742 4.92448C19.9744 5.82433 20.6871 6.88248 21.2123 8.09894C21.7374 9.31538 22 10.6151 22 11.9982C22 13.3813 21.7375 14.6814 21.2126 15.8984C20.6877 17.1154 19.9753 18.174 19.0755 19.0742C18.1757 19.9744 17.1175 20.6871 15.9011 21.2123C14.6846 21.7374 13.3849 22 12.0018 22ZM12 20.4211C14.3509 20.4211 16.3421 19.6053 17.9737 17.9737C19.6053 16.3421 20.4211 14.3509 20.4211 12C20.4211 9.64912 19.6053 7.65788 17.9737 6.0263C16.3421 4.39472 14.3509 3.57893 12 3.57893C9.64912 3.57893 7.65788 4.39472 6.0263 6.0263C4.39472 7.65788 3.57893 9.64912 3.57893 12C3.57893 14.3509 4.39472 16.3421 6.0263 17.9737C7.65788 19.6053 9.64912 20.4211 12 20.4211Z'
          fill='#1FA2C8'
        />
      </svg>
    ),

    helpIcon: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M9.52955 20C3.81172 20 0 15.7054 0 10.0129C0 4.32043 3.81172 0 9.52955 0C15.2479 0 19.0596 4.32043 19.0596 10.0129C19.0596 15.7054 15.2479 20 9.52955 20ZM9.52955 19.0089C14.6125 19.0089 18.0179 15.1466 18.0179 10.0129C18.0179 4.85395 14.6125 0.991056 9.52955 0.991056C4.47242 0.991056 1.04172 4.85395 1.04172 10.0129C1.04172 15.1466 4.47242 19.0089 9.52955 19.0089ZM6.40387 4.57478V3.58321C7.49677 3.27871 8.48783 3.15153 9.40289 3.15153C11.4863 3.15153 12.8336 4.24391 12.8336 6.32787C12.8336 7.52262 12.3254 8.23399 11.2832 9.30156L10.8256 9.75857C10.1649 10.4705 10.0377 10.9021 10.0377 11.5375V12.6557H8.94536V11.4868C8.94536 10.5718 9.09787 9.98759 10.0636 8.9712L10.5206 8.51367C11.385 7.59861 11.6388 7.2176 11.6388 6.3532C11.6388 4.82862 10.7496 4.19325 9.17386 4.19325C8.38598 4.19325 7.36959 4.32043 6.40387 4.57478ZM8.615 15.7054C8.615 15.1207 8.92002 14.8669 9.50421 14.8669C10.1396 14.8669 10.3939 15.1207 10.3939 15.7054C10.3939 16.2901 10.1396 16.5693 9.50421 16.5693C8.92002 16.5693 8.615 16.2901 8.615 15.7054Z'
          fill='#1A1A1A'
        />
      </svg>
    ),

    dateTimeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M20.4775 6.6246V19.8679C20.4775 21.3094 19.7866 22 18.3455 22H5.13208C3.69033 22 3 21.3094 3 19.8679V6.6246C3 5.18315 3.69033 4.49252 5.13208 4.49252H6.7235V2H7.98503V4.49252H15.4925V2H16.7534V4.49252H18.3455C19.7866 4.49252 20.4775 5.18315 20.4775 6.6246ZM5.3123 5.69357C4.53155 5.69357 4.23099 6.02407 4.23099 6.80481V8.27619H19.246V6.80481C19.246 6.02407 18.946 5.69357 18.1653 5.69357H5.3123ZM18.1653 20.7986C18.946 20.7986 19.246 20.4684 19.246 19.6877V9.50748H4.23099V19.6877C4.23099 20.4684 4.53155 20.7986 5.3123 20.7986H18.1653ZM8.91605 13.2912C8.91605 13.9519 8.64543 14.2521 7.9551 14.2521C7.29409 14.2521 6.99414 13.9519 6.99414 13.2912C6.99414 12.6005 7.29409 12.3302 7.9551 12.3302C8.64543 12.3302 8.91605 12.6005 8.91605 13.2912ZM8.91605 17.015C8.91605 17.6757 8.64543 17.9759 7.9551 17.9759C7.29409 17.9759 6.99414 17.6757 6.99414 17.015C6.99414 16.3243 7.29409 16.054 7.9551 16.054C8.64543 16.054 8.91605 16.3243 8.91605 17.015ZM12.6994 13.2912C12.6994 13.9519 12.4294 14.2521 11.7385 14.2521C11.0781 14.2521 10.7775 13.9519 10.7775 13.2912C10.7775 12.6005 11.0781 12.3302 11.7385 12.3302C12.4294 12.3302 12.6994 12.6005 12.6994 13.2912ZM12.6994 17.015C12.6994 17.6757 12.4294 17.9759 11.7385 17.9759C11.0781 17.9759 10.7775 17.6757 10.7775 17.015C10.7775 16.3243 11.0781 16.054 11.7385 16.054C12.4294 16.054 12.6994 16.3243 12.6994 17.015ZM16.4834 13.2912C16.4834 13.9519 16.2134 14.2521 15.5225 14.2521C14.8621 14.2521 14.5615 13.9519 14.5615 13.2912C14.5615 12.6005 14.8621 12.3302 15.5225 12.3302C16.2134 12.3302 16.4834 12.6005 16.4834 13.2912ZM16.4834 17.015C16.4834 17.6757 16.2134 17.9759 15.5225 17.9759C14.8621 17.9759 14.5615 17.6757 14.5615 17.015C14.5615 16.3243 14.8621 16.054 15.5225 16.054C16.2134 16.054 16.4834 16.3243 16.4834 17.015Z'
          fill='#1A1A1A'
        />
      </svg>
    ),

    removeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
            fill='#1A1A1A'
          />
        </g>
      </svg>
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#F2F2F2'
        />
      </svg>
    ),
    addIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12.4734 12.4734V22.6036C12.4734 22.7299 12.4339 22.8403 12.355 22.935C12.2761 23.0297 12.1578 23.077 12 23.077C11.8422 23.077 11.7239 23.0376 11.645 22.9587C11.5661 22.8798 11.5266 22.7614 11.5266 22.6036V12.4734H1.39634C1.27011 12.4734 1.15966 12.4339 1.06498 12.355C0.970305 12.2761 0.922968 12.1578 0.922968 12C0.922968 11.8422 0.962416 11.7239 1.04131 11.645C1.12021 11.5661 1.23855 11.5266 1.39634 11.5266H11.5266V1.39636C11.5266 1.27012 11.5661 1.15967 11.645 1.06499C11.7239 0.970318 11.8422 0.92298 12 0.92298C12.1578 0.92298 12.2761 0.962429 12.355 1.04132C12.4339 1.12022 12.4734 1.23857 12.4734 1.39636V11.5266L22.6036 11.5266C22.7299 11.5266 22.8403 11.5661 22.935 11.645C23.0297 11.7239 23.077 11.8422 23.077 12C23.077 12.1578 23.0376 12.2761 22.9587 12.355C22.8798 12.4339 22.7614 12.4734 22.6036 12.4734L12.4734 12.4734Z'
          fill='#141414'
        />
      </svg>
    ),

    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M11.7088 22H10.1958C7.72337 18.6421 5.21417 14.7303 3 10.5609L4.36551 9.74867C6.4318 13.587 8.64597 17.092 10.8969 20.2291C14.5502 13.9923 17.4283 8.49426 20.528 2L21.967 2.7004C18.646 9.6751 15.6942 15.2843 11.7088 22Z'
          fill='#1A1A1A'
          stroke='#1A1A1A'
          strokeWidth='0.4'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.7088 22H10.1958C7.72337 18.6421 5.21417 14.7303 3 10.5609L4.36551 9.74867C6.4318 13.587 8.64597 17.092 10.8969 20.2291C14.5502 13.9923 17.4283 8.49426 20.528 2L21.967 2.7004C18.646 9.6751 15.6942 15.2843 11.7088 22Z"
        fill="#1A1A1A"
        stroke="#1A1A1A"
        stroke-width="0.4"
      />
    </svg>
`),
    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
            fill='#1A1A1A'
          />
        </g>
      </svg>
    ),

    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#000' stroke='#000' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <svg width='20' height='20' viewBox='0 0 20 26' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M11.7088 22H10.1958C7.72337 18.6421 5.21417 14.7303 3 10.5609L4.36551 9.74867C6.4318 13.587 8.64597 17.092 10.8969 20.2291C14.5502 13.9923 17.4283 8.49426 20.528 2L21.967 2.7004C18.646 9.6751 15.6942 15.2843 11.7088 22Z'
            fill='#EFDF00'
            stroke='#EFDF00'
            stroke-width='0.4'
            transform='translate(12 3) scale(0.5)'
          />
        </svg>
      </>
    ),

    selectIcon: (
      <svg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M11.1001 17.9836C8.26025 14.6817 6.65014 12.5128 4 8.47089L5.65633 7.33783C7.97617 10.9355 9.39657 12.8577 11.9995 15.9631C14.6034 12.8086 16.0229 10.8864 18.3899 7.2887L20 8.47089C17.3489 12.5128 15.7388 14.6817 12.8989 17.9836H11.1001Z'
          fill='#1A1A1A'
        />
      </svg>
    ),

    backIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M6.7288 11.1001C9.89991 8.26025 11.9828 6.65014 15.8646 4L16.9528 5.65633C13.4976 7.97617 11.6516 9.39657 8.66921 11.9995C11.6988 14.6034 13.5448 16.0229 17 18.3899L15.8646 20C11.9828 17.3489 9.89991 15.7388 6.7288 12.8989L6.7288 11.1001Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  priceText: {
    totalPriceColor: variables.colors.primary.main,
    fontWeight: 700,
  },
  messageBox: {
    backgroundColor: "#DBF0F6",
    borderColor: "#DBF0F6",
    dividerColor: variables.colors.divider,
    textAlign: "left",
  },
  skeletonHeaderText: {
    background: theme.palette.secondary.contrastText,
  },
  confirmationPageHeader: {
    fontWeight: "bold",
  },
  CheckoutPage: {
    bottomTotalPriceText: {
      fontWeight: "bold",
    },
    backButton: {
      textTransform: "none" as TextTransformProperty,
      fontWeight: "bold",
      "&:hover": {
        backgroundColor: variables.colors.background,
        color: "#656666",
        textDecoration: "none",
      },
      "&:active": {
        backgroundColor: variables.colors.background,
        color: "#656666",
        textDecoration: "none",
      },
    },
    priceText: {
      color: "#000",
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      textTransform: "none" as TextTransformProperty,
    },
  },
  payment: {
    backgroundColor: "#FFFFFF",
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.dark,
    hoverTextColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(renault);
